import React from "react";
import AppState from "../../../state";
import { Utils } from "../../../services";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  Typography,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TableSortLabel,
  TextField,
  Button
} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { toast } from "../../../components/ds";

// Passivo trabalhista: cálculo dos direitos CLT de quem está ativo na data de
// corte, dividido pelos projetos conforme o rateio do salário. As regras estão
// no servidor (server/comum/PassivoCalculo.php); a tela só escolhe a data e
// os dois percentuais e mostra o resultado.

const ENCARGOS_PADRAO = "35,8";
const PCT_PESSOAS_PADRAO = "100";

const ROTULO_COMPONENTE = {
  ferias_vencidas: "Férias vencidas + 1/3",
  ferias_dobra: "Férias em dobra (concessivo vencido)",
  ferias_proporcionais: "Férias proporcionais + 1/3",
  decimo: "13º proporcional (menos o já pago)",
  encargos_decimo: "Encargos sobre o 13º",
  aviso: "Aviso prévio indenizado",
  projecao_ferias: "Férias do período do aviso",
  projecao_decimo: "13º do período do aviso",
  encargos_rescisao: "Encargos sobre aviso e 13º do aviso",
  multa_fgts: "Multa de 40% do FGTS"
};
const COMPONENTES_CURTO = ["ferias_vencidas", "ferias_dobra", "ferias_proporcionais", "decimo", "encargos_decimo"];
const COMPONENTES_LONGO = ["aviso", "projecao_ferias", "projecao_decimo", "encargos_rescisao", "multa_fgts"];

const ROTULO_AVISO = {
  salario_estimado: "salário estimado",
  sem_salario: "sem salário",
  fgts_estimado: "FGTS estimado"
};

const num = v => parseFloat(v) || 0;
const moeda = v => `R$ ${Utils.float2moeda(num(v))}`;

class RelatorioPassivoTrabalhista extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dt_calculo: null,
      projetos: [],
      relatorio: [],
      semProjeto: null,
      totais: null,
      total_projetos: 0,
      modalOpen: false,
      modalProject: null,
      statusProjeto: "andamento",
      // Caixas da tela. Não são gravadas: valem para o cálculo da vez.
      dataCorte: "",
      encargos: ENCARGOS_PADRAO,
      pctPessoas: PCT_PESSOAS_PADRAO,
      parametros: null,
      orderBy: 'id_projeto',
      order: 'asc'
    };
  }

  componentDidMount() {
    this.loadData();
  }

  montarConsulta = () => {
    const partes = [];
    const { dataCorte, encargos, pctPessoas } = this.state;
    if (dataCorte) partes.push(`data_corte=${encodeURIComponent(dataCorte)}`);
    if (String(encargos).trim() !== "") partes.push(`encargos=${encodeURIComponent(String(encargos).trim())}`);
    if (String(pctPessoas).trim() !== "") partes.push(`pct_pessoas=${encodeURIComponent(String(pctPessoas).trim())}`);
    return partes.length ? `?${partes.join("&")}` : "";
  };

  loadData = async () => {
    try {
      AppState.isLoading = true;
      const res = await fetch(`${AppState.serverAddr}/relatorios/PassivoTrabalhista.php${this.montarConsulta()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Authorization: AppState.token
        }
      });

      if (res.status === 401) {
        AppState.isLoading = false;
        return;
      }

      // Lê como texto antes de converter: se o servidor devolver algo que não
      // é JSON, o erro aparece na tela em vez de ficar só no console.
      const texto = await res.text();
      AppState.isLoading = false;

      let json = null;
      try {
        json = JSON.parse(texto);
      } catch (e) {
        toast.error("Resposta inválida do servidor ao carregar o passivo.");
        console.error("Passivo Trabalhista: resposta não é JSON", texto);
        return;
      }

      if (!res.ok) {
        const msg =
          (json && json.status && json.status.value) ||
          `Erro ${res.status} ao carregar o passivo.`;
        toast.error(msg);
        return;
      }

      if (json && json.data) {
        const full = json.data.projetos || [];
        this.setState(
          {
            dt_calculo: json.data.dt_calculo,
            projetos: full,
            semProjeto: json.data.sem_projeto || null,
            totais: json.data.totais || null,
            parametros: json.data.parametros || null,
            total_projetos: json.data.total_projetos || full.length
          },
          () => {
            this.filtroRelatorio();
          }
        );
        (json.data.avisos || []).forEach(aviso => toast.warning(aviso));
      }
    } catch (err) {
      AppState.isLoading = false;
      toast.error("Não foi possível carregar o Passivo Trabalhista.");
      console.error("Erro ao carregar Passivo Trabalhista", err);
    }
  };

  aplicar = () => {
    this.loadData();
  };

  restaurarPadrao = () => {
    this.setState(
      { dataCorte: "", encargos: ENCARGOS_PADRAO, pctPessoas: PCT_PESSOAS_PADRAO },
      () => this.loadData()
    );
  };

  campoHandleChange = name => event => {
    this.setState({ [name]: event.target.value });
  };

  handleSort = (property) => {
    const isAsc = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({
      order: isAsc ? "desc" : "asc",
      orderBy: property
    });
  };

  getSortedData = () => {
    const { relatorio, order, orderBy } = this.state;

    return [...relatorio].sort((a, b) => {
      const pa = a.projeto || {};
      const pb = b.projeto || {};
      const ra = a.resumo || {};
      const rb = b.resumo || {};
      let valueA, valueB;

      if (orderBy === "id_projeto") {
        valueA = pa.id_projeto || 0;
        valueB = pb.id_projeto || 0;
      } else if (orderBy === "desc_curta") {
        valueA = (pa.desc_curta || "").toLowerCase();
        valueB = (pb.desc_curta || "").toLowerCase();
      } else if (orderBy === "dt_inicio") {
        valueA = pa.dt_inicio || "";
        valueB = pb.dt_inicio || "";
      } else if (orderBy === "passivo_trabalhista" || orderBy === "custo_encerramento") {
        valueA = num(ra[orderBy]);
        valueB = num(rb[orderBy]);
      } else if (orderBy === "df") {
        valueA = pa.id_projeto !== 0 ? num(ra.df) : -1;
        valueB = pb.id_projeto !== 0 ? num(rb.df) : -1;
      }

      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  renderResumoRow = (projeto, chave) => {
    const resumo = projeto.resumo || {};
    const p = projeto.projeto || {};
    const semProjeto = chave === "sem";
    const df = (!semProjeto && resumo.df != null && p.id_projeto !== 0) ? resumo.df : null;

    return (
      <TableRow key={semProjeto ? "sem" : p.id_projeto} style={semProjeto ? { backgroundColor: '#fff8e1' } : null}>
        <TableCell>{semProjeto ? "-" : p.id_projeto}</TableCell>
        <TableCell>
          {semProjeto ? (
            <Tooltip title="Pessoas sem nenhum rateio de salário até a data de corte" placement="top">
              <span>Sem projeto</span>
            </Tooltip>
          ) : p.desc_curta}
        </TableCell>
        <TableCell>{semProjeto ? "-" : this.formatDate(p.dt_inicio)}</TableCell>
        <TableCell>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 4 }}>
            <IconButton
              size="small"
              aria-label={`detalhar-passivo-${semProjeto ? 'sem' : p.id_projeto}`}
              title="Ver detalhamento"
              onClick={() => this.openModal(projeto, semProjeto)}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            <span>{moeda(resumo.passivo_trabalhista)}</span>
          </div>
        </TableCell>
        <TableCell>{moeda(resumo.custo_encerramento)}</TableCell>
        <TableCell>{df != null ? moeda(df) : '-'}</TableCell>
      </TableRow>
    );
  };

  openModal = (projeto, semProjeto) => {
    this.setState({ modalOpen: true, modalProject: { ...projeto, semProjeto: !!semProjeto } });
  }

  closeModal = () => {
    this.setState({ modalOpen: false, modalProject: null });
  }

  filterHandleChange = name => event => {
    this.setState({ [name]: event.target.value }, () => {
      this.filtroRelatorio();
    });
  };

  filtroRelatorio = () => {
    const state = this.state;

    function filtraRelatorio(item) {
      const projeto = item.projeto || {};
      let status = state.statusProjeto;

      if (status === "andamento") status = "Ativo";
      else if (status === "encerrado") status = "Encerrado";

      if (status !== "todos") {
        return projeto.status === status;
      }
      return true;
    }

    if (this.state.projetos) {
      this.setState({ relatorio: this.state.projetos.filter(filtraRelatorio) });
    }
  };

  formatDate(iso) {
    if (!iso) return "-";
    // 'AAAA-MM-DD' é lido direto, sem passar por new Date: new Date('2020-01-01')
    // é meia-noite UTC, que no horário de Brasília ainda é o dia anterior.
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return iso;
  }

  renderModal() {
    const projeto = this.state.modalProject || {};
    const resumo = projeto.resumo || {};
    const det = projeto.detalhamento || {};
    const componentes = det.componentes || {};
    const pessoas = det.pessoas || [];
    const parametros = this.state.parametros || {};

    const bloco = (rotulo, valor, dica, destaque) => (
      <Tooltip title={dica || ""} placement="top">
        <div style={destaque ? { paddingLeft: 24, borderLeft: '1px solid rgba(0,0,0,0.12)' } : null}>
          <Typography variant="caption">{rotulo}</Typography>
          <div style={{ fontWeight: 700, marginTop: 4 }}>{moeda(valor)}</div>
        </div>
      </Tooltip>
    );

    const linhasComponentes = (lista) => lista
      .filter(c => num(componentes[c]) !== 0)
      .map(c => (
        <TableRow key={c}>
          <TableCell>{ROTULO_COMPONENTE[c] || c}</TableCell>
          <TableCell align="right">{moeda(componentes[c])}</TableCell>
        </TableRow>
      ));

    const subtitulo = (texto) => (
      <TableRow>
        <TableCell colSpan={2} style={{ fontWeight: 700, backgroundColor: '#fafafa' }}>{texto}</TableCell>
      </TableRow>
    );

    return (
      <div style={{ padding: 8 }}>
        <Typography variant="subtitle1">Resumo</Typography>
        <div style={{ display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap' }}>
          {bloco('Curto prazo', resumo.curto_prazo, 'Já devido às pessoas hoje: férias, 13º e encargos do 13º')}
          {bloco('Longo prazo', resumo.longo_prazo, 'Só existe se houver desligamento sem justa causa: aviso, multa do FGTS e encargos')}
          {bloco(
            `Custo de encerramento (${Utils.float2moeda(num(parametros.pct_pessoas))}% sem justa causa)`,
            resumo.custo_encerramento,
            'Curto prazo + percentual de demitidos sem justa causa × longo prazo',
            true
          )}
        </div>

        <Typography variant="subtitle1" style={{ marginTop: 24 }}>Composição</Typography>
        <Table padding="dense">
          <TableBody>
            {subtitulo('Curto prazo')}
            {linhasComponentes(COMPONENTES_CURTO)}
            {subtitulo('Longo prazo')}
            {linhasComponentes(COMPONENTES_LONGO)}
          </TableBody>
        </Table>

        <Typography variant="subtitle1" style={{ marginTop: 24 }}>{`Pessoas (${pessoas.length})`}</Typography>
        <Typography variant="caption" color="textSecondary">
          Parte de cada pessoa que cabe a este projeto pelo rateio do salário. "Participação" é quanto do passivo total da pessoa ficou aqui.
        </Typography>
        {pessoas.length === 0 ? (
          <Typography variant="body2" style={{ marginTop: 8 }}>Nenhuma pessoa com passivo neste projeto.</Typography>
        ) : (
          <Table padding="dense">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell align="right">Curto prazo</TableCell>
                <TableCell align="right">Longo prazo</TableCell>
                <TableCell align="right">Encerramento</TableCell>
                <TableCell align="right">Participação</TableCell>
                <TableCell>Observação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pessoas.map(p => (
                <TableRow key={p.cpf_funcionario}>
                  <TableCell>{p.nome_funcionario}</TableCell>
                  <TableCell align="right">{moeda(p.curto_prazo)}</TableCell>
                  <TableCell align="right">{moeda(p.longo_prazo)}</TableCell>
                  <TableCell align="right">{moeda(p.custo_encerramento)}</TableCell>
                  <TableCell align="right">{`${Utils.float2moeda(num(p.participacao) * 100)}%`}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="textSecondary">
                      {(p.avisos || []).map(a => ROTULO_AVISO[a] || a).join(', ')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }

  render() {
    const { orderBy, order, semProjeto } = this.state;
    const sortedRelatorio = this.getSortedData();

    const headCellStyle = {
      fontSize: 15,
      fontWeight: 700,
      paddingTop: 18,
      paddingBottom: 18,
      position: 'sticky',
      top: 0,
      backgroundColor: '#fff',
      zIndex: 10
    };

    const cabecalho = (campo, rotulo, dica) => (
      <TableCell style={headCellStyle}>
        <TableSortLabel
          active={orderBy === campo}
          direction={orderBy === campo ? order : 'asc'}
          onClick={() => this.handleSort(campo)}
        >
          <Tooltip title={dica} placement="top"><span>{rotulo}</span></Tooltip>
        </TableSortLabel>
      </TableCell>
    );

    // O total soma as linhas visíveis e mais a linha "Sem projeto", que
    // aparece em qualquer filtro para o total não esconder ninguém.
    const linhasTotal = semProjeto ? [...this.state.relatorio, semProjeto] : this.state.relatorio;
    const somar = campo => linhasTotal.reduce((t, item) => t + num(item.resumo && item.resumo[campo]), 0);
    const totalDf = this.state.relatorio.reduce((t, item) => {
      const p = item.projeto || {};
      const dfVal = (item.resumo && item.resumo.df != null && p.id_projeto !== 0) ? item.resumo.df : 0;
      return t + num(dfVal);
    }, 0);

    return (
      <Grid style={{ marginTop: "2em" }}>
        <Grid container spacing={16}>
          <Grid item xs={6}>
            <Typography variant="h5">Relatório - Passivo + DF</Typography>
            <Typography variant="body2" color="textSecondary">Data de corte: {this.formatDate(this.state.dt_calculo)}</Typography>
            <Typography variant="caption" style={{ marginTop: 6, display: 'block' }}>
              {`Mostrando ${this.state.relatorio.length} de ${this.state.total_projetos}`}
              {this.state.totais ? ` · ${this.state.totais.pessoas} pessoas CLT no cálculo` : ''}
            </Typography>
          </Grid>

          <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '15px' }}>
            <FormControl style={{ minWidth: 200 }}>
              <InputLabel>Status do Projeto</InputLabel>
              <Select value={this.state.statusProjeto} onChange={this.filterHandleChange("statusProjeto")}>
                <MenuItem key={1} value="andamento">Em Andamento</MenuItem>
                <MenuItem key={2} value="encerrado">Encerrados</MenuItem>
                <MenuItem key={0} value="todos">Todos</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
            <TextField
              label="Data de corte"
              type="date"
              value={this.state.dataCorte}
              onChange={this.campoHandleChange("dataCorte")}
              InputLabelProps={{ shrink: true }}
              helperText="Vazio: último dia do mês anterior"
            />
            <TextField
              label="Encargos (%)"
              value={this.state.encargos}
              onChange={this.campoHandleChange("encargos")}
              style={{ width: 140 }}
              helperText="Sobre 13º e aviso"
            />
            <TextField
              label="Demitidos sem justa causa (%)"
              value={this.state.pctPessoas}
              onChange={this.campoHandleChange("pctPessoas")}
              style={{ width: 220 }}
              helperText="Aplica só no longo prazo"
            />
            <Button variant="contained" color="primary" onClick={this.aplicar} style={{ marginBottom: 20 }}>Aplicar</Button>
            <Button onClick={this.restaurarPadrao} style={{ marginBottom: 20 }}>Restaurar padrão</Button>
          </Grid>

          <Grid item xs={12}>
            <Paper style={{ padding: 16 }}>
              <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {cabecalho('id_projeto', 'ID', 'Identificador do projeto')}
                      {cabecalho('desc_curta', 'Projeto', 'Nome curto do projeto')}
                      {cabecalho('dt_inicio', 'Início', 'Data de início do projeto')}
                      {cabecalho('passivo_trabalhista', 'Passivo Trabalhista', 'Já devido às pessoas na data de corte: férias, 13º e encargos do 13º (curto prazo)')}
                      {cabecalho('custo_encerramento', 'Custo de Encerramento', 'Curto prazo + percentual de demitidos sem justa causa × longo prazo (aviso, multa do FGTS e encargos)')}
                      {cabecalho('df', 'DF', 'Despesa financeira acumulada até a data de corte')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRelatorio.map(p => this.renderResumoRow(p))}
                    {semProjeto ? this.renderResumoRow(semProjeto, "sem") : null}
                  </TableBody>
                  <TableFooter>
                    <TableRow style={{ backgroundColor: '#f5f5f5', fontWeight: 700 }}>
                      <TableCell colSpan={3} style={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell style={{ fontWeight: 700, fontSize: '15px' }}>{moeda(somar('passivo_trabalhista'))}</TableCell>
                      <TableCell style={{ fontWeight: 700, fontSize: '15px' }}>{moeda(somar('custo_encerramento'))}</TableCell>
                      <TableCell style={{ fontWeight: 700, fontSize: '15px' }}>{moeda(totalDf)}</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </Paper>
          </Grid>
        </Grid>

        {this.state.modalOpen && (
          <Dialog fullWidth maxWidth="md" open={this.state.modalOpen} onClose={this.closeModal}>
            <DialogTitle>
              {this.state.modalProject && (this.state.modalProject.semProjeto
                ? 'Detalhamento - Passivo Trabalhista - Sem projeto'
                : `Detalhamento - Passivo Trabalhista - Projeto ${this.state.modalProject.projeto.id_projeto}`)}
              <IconButton aria-label="close" style={{ position: 'absolute', right: 8, top: 8 }} onClick={this.closeModal}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
              {this.state.modalProject && this.renderModal()}
            </DialogContent>
          </Dialog>
        )}

      </Grid>
    );
  }
}

export default RelatorioPassivoTrabalhista;
